import { isPlatformServer } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable, PLATFORM_ID, SecurityContext, inject } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { HOST, WpPost, WpPostSummary, WpCategory } from '../interfaces';
import { observableValue } from '../utils/observable-value';

@Injectable({ providedIn: 'root' })
export class PageService {
  private readonly http = inject(HttpClient);
  private readonly domSanitizer = inject(DomSanitizer);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly host = inject(HOST, { optional: true });

  private wpUrl(path: string): string {
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    const relativeUrl = `/api/wp${normalizedPath}`;
    if (!isPlatformServer(this.platformId)) {
      return relativeUrl;
    }
    return `http://${this.host || 'localhost:4000'}${relativeUrl}`;
  }

  public async getInlineStyles(): Promise<string> {
    return observableValue<string>(
      this.http.get(this.wpUrl('/styles/inline.css'), { responseType: 'text' }),
    );
  }

  public async getCategories(): Promise<WpCategory[]> {
    const res = await observableValue<WpCategory[]>(
      this.http.get<WpCategory[]>(this.wpUrl('/categories')),
    );
    if (!res || res.length === 0) {
      return [];
    }
    return res.map((cat: WpCategory) => ({
      ...cat,
      link: cat.link ? cat.link.replace(/^https?:\/\/rasmus\.com/, '') : '',
    }));
  }

  /** Get post from same-origin proxy. */
  public async getPost(slug: string): Promise<WpPost> {
    const res = await observableValue<WpPost[]>(
      this.http.get<WpPost[]>(this.wpUrl(`/post/${encodeURIComponent(slug)}`)),
    );
    if (!res || res.length === 0) {
      throw new Error('No post found');
    }
    const [post] = res;
    return post;
  }

  public async getPosts(): Promise<WpPostSummary[]> {
    const res = await observableValue<WpPost[]>(
      this.http.get<WpPost[]>(this.wpUrl('/posts')),
    );
    if (!res || res.length === 0) {
      return [];
    }
    return res.map((post: WpPost) => ({
      categories: post.categories,
      title: post.title.rendered,
      author: post.author_info.display_name,
      date: new Date(post.date).getTime(),
      snippet: this.domSanitizer.sanitize(SecurityContext.HTML, post.excerpt.rendered) || '',
      link: post.link,
    }));
  }
}
